import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Container, Box, Typography, Button, CircularProgress } from "@mui/material";
import { BsArrowLeft } from "react-icons/bs";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";

export default function RouteMap() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useSelector((state) => state?.loginState?.auth);

  const [loading, setLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    const fetchWorkData = async () => {
      try {
        const workResponse = await axios.get(`${process.env.REACT_APP_URL}work/${id}/`, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        const work = workResponse.data?.data || workResponse.data;

        const tasksResponse = await axios.get(`${process.env.REACT_APP_URL}work/task/0/?filter_by={"work_id":${id}}&limit=200`, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        const taskData = tasksResponse.data?.data || tasksResponse.data;
        const tasks = taskData?.results || taskData;

        // Auto-layout using dagre
        const dagreGraph = new dagre.graphlib.Graph();
        dagreGraph.setDefaultEdgeLabel(() => ({}));
        dagreGraph.setGraph({ rankdir: "LR", nodesep: 60, ranksep: 100 });

        const initialNodes = [];
        const initialEdges = [];

        // 1. Client Node
        const clientId = `client-${work.id}`;
        initialNodes.push({
          id: clientId,
          data: { label: `Client\n${work.client_name}` },
          position: { x: 0, y: 0 },
          type: "input",
          sourcePosition: 'right',
          targetPosition: 'left',
          style: {
            background: "#475569",
            color: "#fff",
            border: "1px solid #334155",
            width: 150,
            borderRadius: "8px",
            padding: "10px",
            fontWeight: "bold",
            textAlign: "center"
          },
        });
        dagreGraph.setNode(clientId, { width: 150, height: 60 });

        // 2. Service Node
        const serviceId = `service-${work.id}`;
        initialNodes.push({
          id: serviceId,
          data: { label: `Service\n${work.service_name}` },
          position: { x: 0, y: 0 },
          sourcePosition: 'right',
          targetPosition: 'left',
          style: {
            background: "#3b82f6",
            color: "#fff",
            border: "1px solid #2563eb",
            width: 150,
            borderRadius: "8px",
            padding: "10px",
            fontWeight: "bold",
            textAlign: "center"
          },
        });
        dagreGraph.setNode(serviceId, { width: 150, height: 60 });

        initialEdges.push({
          id: `e-${clientId}-${serviceId}`,
          source: clientId,
          target: serviceId,
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed },
        });
        dagreGraph.setEdge(clientId, serviceId);

        // 3. Work Node
        const deadlineDate = work.expected_completion_date ? new Date(work.expected_completion_date).toLocaleDateString() : 'N/A';
        const workNodeId = `work-${work.id}`;

        initialNodes.push({
          id: workNodeId,
          data: { label: `Work\n${work.work_name}\nFY: ${work.financial_year}\nDeadline: ${deadlineDate}` },
          position: { x: 0, y: 0 },
          sourcePosition: 'right',
          targetPosition: 'left',
          style: {
            background: "#8b5cf6",
            color: "#fff",
            border: "1px solid #7c3aed",
            width: 170,
            borderRadius: "8px",
            padding: "10px",
            fontWeight: "bold",
            textAlign: "center"
          },
        });
        dagreGraph.setNode(workNodeId, { width: 170, height: 90 });

        initialEdges.push({
          id: `e-${serviceId}-${workNodeId}`,
          source: serviceId,
          target: workNodeId,
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed },
        });
        dagreGraph.setEdge(serviceId, workNodeId);

        // 3.5 Dynamic File Nodes
        let docsData = [];
        if (work.repo_uuid) {
          try {
            let docsUrl = `${process.env.REACT_APP_REPO_URL}repo/documents/0/?repository__client_id=${work.repo_uuid}`;
            if (work.files && Array.isArray(work.files) && work.files.length > 0) {
              docsUrl += `&id__in=${JSON.stringify(work.files)}`;
            }
            const docsResponse = await axios.get(docsUrl, {
              headers: { Authorization: `Bearer ${auth?.token}` }
            });
            docsData = docsResponse.data?.data?.results || docsResponse.data?.results || docsResponse.data || [];
            if (!Array.isArray(docsData)) docsData = [];
          } catch (e) {
            console.error("Failed to fetch documents", e);
          }
        }

        if (docsData.length > 0) {
          docsData.forEach((doc, dIndex) => {
            const fileNodeId = `file-${doc.id}`;
            const createDate = doc.created_at ? doc.created_at.split("T")[0] : 'N/A';
            const commitMsg = doc.commit_message || 'No message';
            initialNodes.push({
              id: fileNodeId,
              data: { label: `📄 ${doc.original_filename || doc.repo_name || 'Document'}\n📅 ${createDate}\n✨ ${commitMsg}` },
              position: { x: 0, y: 0 },
              type: "output",
              sourcePosition: 'right',
              targetPosition: 'left',
              style: {
                background: "#f59e0b",
                color: "#fff",
                border: "1px solid #d97706",
                borderRadius: "8px",
                padding: "10px",
                width: 190,
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "12px",
                wordWrap: "break-word"
              },
            });
            dagreGraph.setNode(fileNodeId, { width: 190, height: 90 });

            initialEdges.push({
              id: `e-${workNodeId}-${fileNodeId}`,
              source: workNodeId,
              target: fileNodeId,
              animated: true,
              style: { stroke: "#f59e0b", strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, color: "#f59e0b" },
            });
            dagreGraph.setEdge(workNodeId, fileNodeId);
          });
        }

        // 4. Tasks Nodes
        if (tasks && Array.isArray(tasks)) {
          tasks.forEach((task, tIndex) => {
            const taskNodeId = `task-${task.id}`;
            let bgColor = "#f1f5f9";
            let color = "#1e293b";
            if (task.status === "completed") { bgColor = "#22c55e"; color = "#fff"; }
            else if (task.status === "in_progress") { bgColor = "#f59e0b"; color = "#fff"; }
            else if (task.status === "review") { bgColor = "#0ea5e9"; color = "#fff"; }
            else if (task.status === "approved") { bgColor = "#16a34a"; color = "#fff"; }
            else if (task.status === "rejected") { bgColor = "#ef4444"; color = "#fff"; }

            initialNodes.push({
              id: taskNodeId,
              data: { label: `Task: ${task.task_name}\nStatus: ${task.status.toUpperCase()}` },
              position: { x: 0, y: 0 },
              type: "output",
              sourcePosition: 'right',
              targetPosition: 'left',
              style: {
                background: bgColor,
                color: color,
                border: `1px solid ${bgColor}`,
                borderRadius: "8px",
                padding: "8px",
                width: 150,
                textAlign: "center"
              },
            });
            dagreGraph.setNode(taskNodeId, { width: 150, height: 60 });

            initialEdges.push({
              id: `e-${workNodeId}-${taskNodeId}`,
              source: workNodeId,
              target: taskNodeId,
              markerEnd: { type: MarkerType.ArrowClosed },
            });
            dagreGraph.setEdge(workNodeId, taskNodeId);
          });
        }

        // Layout nodes
        dagre.layout(dagreGraph);

        initialNodes.forEach((node) => {
          const nodeWithPosition = dagreGraph.node(node.id);
          node.position = {
            x: nodeWithPosition.x - 75,
            y: nodeWithPosition.y - 30,
          };
        });

        setNodes(initialNodes);
        setEdges(initialEdges);
      } catch (err) {
        console.error("Failed to fetch work routemap data", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchWorkData();
  }, [id, auth?.token, setNodes, setEdges]);

  return (
    <Container maxWidth="false" sx={{ height: "calc(100vh - 100px)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Button startIcon={<BsArrowLeft />} onClick={() => navigate("/works/")}>Back to Works</Button>
        <Typography variant="h6" fontWeight="bold">Work RouteMap (Visual Mindmap)</Typography>
      </Box>

      <Box sx={{ flex: 1, backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            fitView
            attributionPosition="bottom-right"
          >
            <MiniMap />
            <Controls />
            <Background color="#ccc" gap={16} />
          </ReactFlow>
        )}
      </Box>
    </Container>
  );
}
